import { DateTime } from 'luxon';

import ThrottledTaskRunner from './ThrottledTaskRunner';

export default class LocalCache {
  static cleanupTaskRunner = new ThrottledTaskRunner(LocalCache.clean, 100);

  static set(key, value, ttl = DateTime.local().plus({ years: 10 })) {
    if (typeof window === 'undefined') {
      return;
    }

    LocalCache.dispatchCleaner();
    try {
      window.localStorage.setItem(key, JSON.stringify({ expiresOn: ttl, value }));
      LocalCache.addKeyToIndex(key);
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (e) {
      console.log('Cache is full :(');

      // Delete the biggest object.
      const [bigKey] = Object.keys(window.localStorage)
        .filter((x) => x !== 'cache-index')
        .sort((a, b) => window.localStorage[b].length - window.localStorage[a].length);
      console.log(`Biggest object is ${bigKey}`);
      window.localStorage.removeItem(bigKey);
      LocalCache.dispatchCleaner(); // Remove orphan key.

      // Retry
      LocalCache.set(key, value, ttl);
    }
  }

  static get(key) {
    if (typeof window === 'undefined') {
      return null;
    }

    LocalCache.dispatchCleaner();
    const obj = window.localStorage.getItem(key);

    if (obj != null) {
      try {
        if (JSON.parse(obj).expiresOn != null && DateTime.fromISO(JSON.parse(obj).expiresOn) >= DateTime.local()) {
          return JSON.parse(obj).value;
        }
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {}
      // Migrate the object.
      try {
        const parsed = JSON.parse(obj);
        LocalCache.set(key, parsed);
        return parsed;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {}
      LocalCache.set(key, obj);
      return obj;
    }
    return null;
  }

  static get index() {
    if (typeof window === 'undefined') {
      return [];
    }

    return JSON.parse(window.localStorage.getItem('cache-index') || '[]');
  }

  static addKeyToIndex(key) {
    if (typeof window === 'undefined') {
      return;
    }

    // Check if key is already in index.
    if (LocalCache.index != null && LocalCache.index.includes(key)) {
      return;
    }
    window.localStorage.setItem(
      'cache-index',
      JSON.stringify(LocalCache.index == null ? [key] : [...LocalCache.index, key]),
    );
  }

  static remove(key) {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.removeItem(key);
    const cacheIndex = LocalCache.index.find((x) => x === key);
    if (cacheIndex) {
      const activeItems = LocalCache.index.filter((x) => x !== key);
      window.localStorage.setItem('cache-index', JSON.stringify(activeItems));
    }
  }

  static dispatchCleaner() {
    if (typeof window === 'undefined') {
      return;
    }
    LocalCache.cleanupTaskRunner.run();
  }

  static clean() {
    if (typeof window === 'undefined') {
      return;
    }

    if (LocalCache.index != null) {
      const activeItems = LocalCache.index.filter(
        (x) =>
          window.localStorage.getItem(x) != null &&
          DateTime.fromISO(JSON.parse(window.localStorage.getItem(x)).expiresOn) >= DateTime.local(),
      );

      //
      // Delete old data.
      //
      const toRemove = LocalCache.index.filter((x) => !activeItems.includes(x));
      toRemove.forEach((key) => {
        if (window.localStorage[key] != null) {
          LocalCache.remove(key);
        }
      });

      window.localStorage.setItem('cache-index', JSON.stringify(activeItems));
    }
  }
}
